import { Component, inject, Inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon'
import { MatExpansionModule } from '@angular/material/expansion'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatMenuModule } from '@angular/material/menu'
import { MatInputModule } from '@angular/material/input'
import { MatFormField } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider'
import { MatChipsModule } from '@angular/material/chips'
import { AsyncPipe, CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, tap,filter } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EncounterServiceService } from '../../patient-wrapper/encounter-service.service';
import { UtilityService } from '../../shared/utility.service';
import { AuthService } from '../../shared/auth/auth.service';
import { backendEndPointToken } from '../../app.config';
import { LoadingUIEnabled } from '../../loading.interceptor';


@Component({
  selector: 'app-appointment-details',

  imports: [MatCardModule, MatButtonModule,
    MatFormField, MatDividerModule, DatePipe, RouterLink,
    RouterLinkActive, CommonModule,
    MatExpansionModule, MatCheckboxModule, TitleCasePipe,
    MatTableModule, AsyncPipe,
    MatChipsModule,
    MatInputModule,
    MatMenuModule,
    MatTableModule, MatIconModule, ReactiveFormsModule],
  host: {

  },

  templateUrl: './appointment-details.component.html',
  styleUrl: '../appointment.component.scss',
})
export class AppointmentDetailsComponent {
  encounterService = inject(EncounterServiceService);
    private utilityService = inject(UtilityService);

    authService = inject(AuthService);
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    canInitiateEncounter$?: Observable<boolean>

  private readonly meetingUrlRegex = /(https?:\/\/[^\s]+)/i;

  appointment: any;
   participantNames: Map<string, string> = new Map();
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dref: MatDialogRef<AppointmentDetailsComponent>
  ) {
    this.appointment = data?.resourceData || data || {};
    console.log('📋 Appointment details data:', this.appointment);
  }

  ngOnInit(): void {
    this.resolveParticipantNames();
    this.canInitiateEncounter$ = this.authService.user.pipe(
      map(() => this.authService.can('encounter', 'add'))
    );
  }

resolveParticipantNames(): void {
  this.appointment.participant?.forEach((p: any) => {
    const ref = p.actor?.reference;
    if (!ref) return;

    const [resourceType, id] = ref.split('/');
    if (!id) return;

    if (p.actor?.display) {
      this.participantNames.set(ref, p.actor.display);
      return;
    }

    this.utilityService.threeTierQueryAndSyncToState$({
      resourceType,
      urlSearchObject: { _id: id },
      scope: 'orgWide'
    }).pipe(
      map((resources: any[]) => resources?.[0]),
      filter((resource: any) => !!resource) // ← skip empty emissions
    ).subscribe({
      next: (resource: any) => {
        const n = resource.name?.[0];
        const name = [n?.given?.join(' '), n?.family].filter(Boolean).join(' ') || ref;
        this.participantNames.set(ref, name);
        this.participantNames = new Map(this.participantNames); // ← trigger change detection
      }
    });
  });
}

  getParticipantName(ref: string): string {
    return this.participantNames.get(ref) || ref;
  }

  getPatientParticipant(): any {
    return this.appointment.participant?.find((p: any) => 
      p.actor?.reference?.includes('Patient/')
    );
  }

  getPractitionerParticipant(): any {
    return this.appointment.participant?.find((p: any) => 
      p.actor?.reference?.includes('Practitioner/')
    );
  }
  router = inject(Router);

  getDurationMinutes(): number | null {
    if (this.appointment.minutesDuration) return this.appointment.minutesDuration;
    if (this.appointment.start && this.appointment.end) {
      const diff = new Date(this.appointment.end).getTime() - new Date(this.appointment.start).getTime();
      return Math.round(diff / 60000);
    }
    return null
  }

  hasMeetingLink(): boolean {
    return !!this.getMeetingLink();
  }

  openMeetingLink(): void {
    const appointmentId = (this.appointment?.id ?? '').toString().trim();
    if (!appointmentId) return;

    void this.navigateToZoomMeeting(appointmentId);
  }

  private async navigateToZoomMeeting(appointmentId: string): Promise<void> {
    const patientId = this.extractPatientId(this.getPatientParticipant()?.actor?.reference);
    const currentRole = String(this.authService.user.getValue()?.role ?? '').toLowerCase();
    const meetingRole = currentRole === 'patient' ? 0 : 1;

    if (patientId && await this.patientExistsInCurrentTenant(patientId)) {
      this.router.navigate(
        ['/app/patients', patientId, 'zoom-meeting'],
        { queryParams: { role: meetingRole, appointmentId } }
      );
      return;
    }

    this.router.navigate(
      ['/app/zoom-meetings', appointmentId],
      { queryParams: { role: meetingRole, patientId: patientId || undefined, patientContextMissing: patientId ? true : undefined } }
    );
  }

  private getMeetingLink(): string {
    const appointment: any = this.appointment;
    if (!appointment) return '';

    const direct = this.normalizeMeetingUrl(
      appointment.zoomMeetingUrl || appointment.meetingLink || appointment.meetingUrl
    );
    if (direct) return direct;

    const ext = Array.isArray(appointment.extension) ? appointment.extension : [];
    for (const ex of ext) {
      const url = this.normalizeMeetingUrl(ex?.valueUrl || ex?.valueUri || ex?.valueString);
      if (url) return url;
    }

    const textCandidates: string[] = [
      appointment.comment,
      appointment.patientInstruction,
      appointment.description
    ].filter((v: any) => typeof v === 'string' && !!v.trim());

    for (const text of textCandidates) {
      const match = text.match(this.meetingUrlRegex);
      if (match?.[1]) {
        const url = this.normalizeMeetingUrl(match[1]);
        if (url) return url;
      }
    }

    return '';
  }

  private normalizeMeetingUrl(value: any): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) return '';
    const withProtocol = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
    try {
      const u = new URL(withProtocol);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.toString();
    } catch {
      return '';
    }
  }

  private extractPatientId(patientRef: string | null | undefined): string {
    if (!patientRef) {
      return '';
    }

    const normalized = patientRef.split('?')[0].trim();
    const segments = normalized.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  }

  private async patientExistsInCurrentTenant(patientId: string): Promise<boolean> {
    try {
      const bundle = await firstValueFrom(
        this.http.get<any>(`${this.backendUrl}/Patient?_id=${encodeURIComponent(patientId)}&_count=1`, {
          context: new HttpContext().set(LoadingUIEnabled, false)
        }).pipe(catchError(() => of(null)))
      );
      return !!bundle?.entry?.some((entry: any) => entry.resource?.id === patientId);
    } catch {
      return false;
    }
  }
}
